import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import {
  ChatPresenceMap, ChatPresenceStatus, ChatSupervisionFilter, ChatSupervisionKind, ChatUser, isStaffRow
} from '../../chat.model';

/** Two views for whoever supervises (2026-10-26): my own conversations, and the team's. */
export type ChatListView = 'mine' | 'supervision';
/** Footer tab of "As minhas": conversations with clients, or with colleagues. */
export type ChatMineSegment = 'clients' | 'staff';
/** Chips of "As minhas": every conversation of the tab, or the unread ones. */
export type ChatMineFilter = 'all' | 'unread';

const VIEW_KEY = 'imodigi-chat-list-view';
const MINE_SEGMENT_KEY = 'imodigi-chat-list-segment';
const SUPERVISION_SEGMENT_KEY = 'imodigi-chat-list-supervision-segment';

/**
 * The conversation list. Redone on 2026-10-26 (two views) and again on
 * 2026-10-27 after the user tested it:
 *
 *  - The footer **Clientes | Consultores** is back: it picks what the list is
 *    about. The chips above it are **filters** of that tab (Todas · Não
 *    lidas), never a second way of choosing clients or colleagues.
 *  - Whoever supervises (`canSupervise`: director, store operator, master)
 *    also has **"Supervisão"**, with the same idea: the footer is
 *    **Clientes | Entre consultores** (each with a tooltip saying what it
 *    holds) and the chips filter (Todas · À espera de resposta, clients only).
 *    That view is `chat-supervision`: consultants closed, 10 at a time, more on
 *    scroll, the search on the server. The list (`conversations`) no longer
 *    carries the team's conversations at all.
 *
 * The container filters `conversations` and `queue` by the search; view and
 * tabs are kept for the session.
 */
@Component({
  selector: 'chat-conversation-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-conversation-list.component.html',
  styleUrls: ['./chat-conversation-list.component.css']
})
export class ChatConversationListComponent implements OnChanges {

  @ViewChild('searchInput', { static: true }) searchInput!: ElementRef<HTMLInputElement>;

  @Input() conversations: ChatUser[] = [];
  /** `GET /chat/queue` (only with canAssign): oldest waiting first. */
  @Input() queue: ChatUser[] = [];
  @Input() activeKey: string | null = null;
  @Input() presence: ChatPresenceMap = {};
  @Input() online: string[] = [];
  @Input() loading = false;
  @Input() canAssign = false;
  /** Director / store operator / master: the "Supervisão" view exists. */
  @Input() canSupervise = false;
  @Input() canSetAway = false;
  @Input() myPresence: 'online' | 'away' = 'online';
  /** Nome e foto do próprio, para o avatar do cabeçalho. */
  @Input() meName?: string;
  @Input() mePhoto?: string;
  /** Link "Definições do chat" no menu do avatar (null esconde). */
  @Input() settingsLink: any[] | null = null;
  /** Balão: link "Abrir a página do chat" no menu do avatar. */
  @Input() pageLink: any[] | null = null;
  /** Não lidas de TODA a lista (sem pesquisa): clientes e colegas. */
  @Input() unreadTotals: { clients: number; staff: number } | null = null;
  /** Supervisão: base das fotos e a normalização das linhas do contentor. */
  @Input() profileBase = '';
  @Input() normalizeRow: (row: any) => ChatUser = (row: any) => row;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @Output() select = new EventEmitter<ChatUser>();
  @Output() search = new EventEmitter<string>();
  @Output() setPresence = new EventEmitter<'online' | 'away'>();
  /** "Nova conversa com um colega": começar uma conversa privada com um colega. */
  @Output() newConversation = new EventEmitter<void>();

  view: ChatListView = 'mine';
  mineSegment: ChatMineSegment = 'clients';
  mineFilter: ChatMineFilter = 'all';
  supervisionSegment: ChatSupervisionKind = 'clients';
  supervisionFilter: ChatSupervisionFilter = 'all';

  /** "As minhas": the rows of the tab and filter on screen. */
  visible: ChatUser[] = [];
  /** Rows of the tab (Todas) and the unread ones of the tab (Não lidas). */
  segmentCount = 0;
  segmentUnread = 0;
  clientsUnread = 0;
  staffUnread = 0;
  /** Conversations to assign: the accent badge on "Supervisão". */
  supervisionAttention = 0;

  /** The search as typed: "Supervisão" sends it to the server. */
  searchTerm = '';
  menuOpen = false;
  hasQuery = false;
  /** Widths (%) of the name line of each skeleton row. */
  readonly skeletonRows = [72, 56, 80, 62, 48, 68];

  constructor(private host: ElementRef<HTMLElement>) {
    this.view = readSession(VIEW_KEY) === 'supervision' ? 'supervision' : 'mine';
    this.mineSegment = readSession(MINE_SEGMENT_KEY) === 'staff' ? 'staff' : 'clients';
    this.supervisionSegment = readSession(SUPERVISION_SEGMENT_KEY) === 'staff' ? 'staff' : 'clients';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['canSupervise'] && !this.canSupervise && this.view === 'supervision') {
      this.view = 'mine';
    }
    this.recompute();
  }

  /** Nothing to show but a skeleton: the first list has not arrived. */
  get showSkeleton(): boolean {
    return this.view === 'mine' && this.loading && this.visible.length === 0;
  }

  get isEmpty(): boolean {
    return this.view === 'mine' && !this.loading && this.visible.length === 0;
  }

  setView(view: ChatListView): void {
    this.view = view;
    writeSession(VIEW_KEY, view);
    this.recompute();
  }

  setMineSegment(segment: ChatMineSegment): void {
    this.mineSegment = segment;
    writeSession(MINE_SEGMENT_KEY, segment);
    this.recompute();
  }

  setMineFilter(filter: ChatMineFilter): void {
    this.mineFilter = filter;
    this.recompute();
  }

  setSupervisionSegment(segment: ChatSupervisionKind): void {
    this.supervisionSegment = segment;
    writeSession(SUPERVISION_SEGMENT_KEY, segment);
    if (segment === 'staff') {
      this.supervisionFilter = 'all';
    }
  }

  setSupervisionFilter(filter: ChatSupervisionFilter): void {
    this.supervisionFilter = filter;
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value || '';
    this.hasQuery = value.trim().length > 0;
    this.searchTerm = value;
    this.search.emit(value);
  }

  clearSearch(): void {
    const el = this.searchInput.nativeElement;
    el.value = '';
    this.hasQuery = false;
    this.searchTerm = '';
    this.search.emit('');
    el.focus();
  }

  choosePresence(status: 'online' | 'away'): void {
    this.menuOpen = false;
    if (status !== this.myPresence) {
      this.setPresence.emit(status);
    }
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  presenceOf(c: ChatUser): ChatPresenceStatus | null {
    if (!c.userId) {
      return null;
    }
    const known = this.presence[c.userId];
    if (known) {
      return known;
    }
    return this.online.indexOf(c.userId) > -1 ? 'online' : null;
  }

  /** A row is "active" by chat id, or by user + property while the chat has no id yet. */
  keyOf(c: ChatUser): string {
    return c.chat ? 'chat:' + c.chat : 'user:' + (c.userId || '') + ':' + (c.propertyReference || c.propertyId || '');
  }

  trackByKey = (_i: number, c: ChatUser) => this.keyOf(c);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.menuOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.menuOpen = false;
  }

  private recompute(): void {
    // "As minhas": as conversas em que participo (o servidor já não manda as da equipa).
    const mine = (this.conversations || []).filter(c => !c.supervised);
    const clients = mine.filter(c => !isStaffRow(c) && (!!c.chat || !!c.userId));
    // Consultores: as conversas com colegas e, depois delas, os colegas sem conversa
    // (é por aqui que se começa uma).
    const staff = mine.filter(c => isStaffRow(c));
    const totals = this.unreadTotals;
    this.clientsUnread = totals ? totals.clients : clients.filter(c => c.unreadCount > 0).length;
    this.staffUnread = totals ? totals.staff : staff.filter(c => c.unreadCount > 0).length;

    const rows = this.mineSegment === 'staff' ? staff : clients;
    const unread = rows.filter(c => c.unreadCount > 0);
    this.segmentCount = this.mineSegment === 'staff' ? staff.filter(c => !!c.chat).length : rows.length;
    this.segmentUnread = unread.length;
    this.visible = this.mineFilter === 'unread' ? unread : rows;

    this.supervisionAttention = this.canAssign ? (this.queue || []).length : 0;
  }
}

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    /* no storage */
  }
}
